import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const AdminAuthGuard = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const hasCheckedAuth = useRef(false);
  
  // Global flag to prevent multiple redirects
  if (!window.adminRedirectInProgress) {
    window.adminRedirectInProgress = false;
  }

  const checkAuth = useCallback(() => {
    const isAuth = sessionStorage.getItem('admin_authenticated') === 'true';
    const loginTime = sessionStorage.getItem('admin_login_time');
    
    if (!isAuth || !loginTime) {
      // Use direct navigation with small delay to prevent browser throttling
      // Only redirect if no redirect is in progress
      if (!window.adminRedirectInProgress) {
        window.adminRedirectInProgress = true;
        setTimeout(() => {
          navigate('/admin/login');
          // Reset flag after navigation
          setTimeout(() => {
            window.adminRedirectInProgress = false;
          }, 1000);
        }, 50);
      }
      return false;
    }

    // Check if session is expired (24 hours)
    const loginDate = new Date(loginTime);
    const now = new Date();
    const hoursDiff = (now - loginDate) / (1000 * 60 * 60);
    
    if (hoursDiff > 24) {
      sessionStorage.removeItem('admin_authenticated');
      sessionStorage.removeItem('admin_login_time');
      // Use direct navigation with small delay to prevent browser throttling
      // Only redirect if no redirect is in progress
      if (!window.adminRedirectInProgress) {
        window.adminRedirectInProgress = true;
        setTimeout(() => {
          navigate('/admin/login');
          // Reset flag after navigation
          setTimeout(() => {
            window.adminRedirectInProgress = false;
          }, 1000);
        }, 50);
      }
      return false;
    }

    return true;
  }, [navigate]);

  useEffect(() => {
    // AdminAuthGuard effect
    if (hasCheckedAuth.current) {
      // Auth already checked, skipping
      return;
    }
    
    hasCheckedAuth.current = true;
    const isAuth = checkAuth();
    // Auth check result
    if (isAuth) {
      // Setting isAuthenticated to true
      setIsAuthenticated(true);
    }
    // Setting isLoading to false
    setIsLoading(false);
  }, [checkAuth]);

  if (isLoading) {
    // Showing loading spinner
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-white text-lg">Verifying access...</p>
        </motion.div>
      </div>
    );
  }

  // Render check

  if (!isAuthenticated) {
    // Not authenticated, returning null
    return null;
  }

  // Rendering children
  return children;
};

export default AdminAuthGuard;
